import cx from "classnames";
import { useEffect, useState } from "react";

import { Button, Flex, Icon, Table, Text } from "@theorgchartdev/storybook";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { CurrentMetricText } from "@/appComponents/Navigation/RightSidebar/Planning/Dashboard/CurrentMetricText";
import { TargetMetricInput } from "@/appComponents/Navigation/RightSidebar/Planning/Dashboard/TargetMetricInput";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { GetDoc } from "@/Utils/StateLib";
import { GetFieldDefFromName } from "@/Utils/DataLib";
import { enumObjClassNames } from "@coreapp/constants";
import { getObjClassNameForField } from "@/Utils/Plan/MetricsUtils";

export const MetricsTable = () => {
  const {
    planManager,
    manageMetricsContextValue: { setShowManageMetrics }
  } = usePlanManagerContext();

  const { chartDocument } = useChartDocumentContext();
  const { wsMgr } = useWSMgrContext();
  const [metricsData, setMetricsData] = useState({ data: [] });

  const planMgr = planManager.state;
  const doc = GetDoc();
  const objClassName = doc?.IsPositionDocument()
    ? enumObjClassNames.position
    : enumObjClassNames.box;

  const { canManageMetrics } = getPlanPermissions({ useCurrentPlan: true });

  const onManageMetrics = () => {
    setShowManageMetrics(true);
  };

  const RebuildMetricsData = () => {
    let newTableData = {
      data: BuildTableData()
    };
    setMetricsData(newTableData);
  };

  const BuildTableData = () => {
    let planInfo = planMgr?.GetPlanInfo();
    if (!planMgr || !planInfo) return [];

    let dataMetrics = planInfo["metrics"];
    let planRootObj = planMgr.GetTopObjRef();

    return dataMetrics.map(aMetric => {
      let normalizedMetric = {
        metric: aMetric,
        name: "",
        starting: "",
        current: "",
        target: "",
        fieldDef: null
      };

      if (!aMetric["fieldID"]) return normalizedMetric;
      let fieldID = aMetric["fieldID"].replaceAll(" ", "");

      // GetFieldValue(sourceDef, false)       → planning value (via _metrics_ planning def)
      // GetFieldValue(sourceDef, false, true) → base value (raw field, pre-plan chart value)
      let sourceDef = planRootObj?.GetFieldDefByID(fieldID);

      if (sourceDef == null && planRootObj) {
        sourceDef = GetFieldDefFromName(
          fieldID,
          getObjClassNameForField(aMetric["fieldID"], null, objClassName)
        );
      }

      if (sourceDef == null || !planRootObj) return normalizedMetric;

      normalizedMetric.fieldDef = sourceDef;
      normalizedMetric.name = sourceDef.GetFieldLabel();
      const currentVal = planRootObj.GetFieldValue(sourceDef, false);

      // baseVal reads the raw (non-_metrics_) field, bypassing the planning def redirect.
      // For formula metrics this is the original chart value; for structural fields (like
      // "Position Count") this is the actual structural count computed by the server.
      const baseVal = planRootObj.GetFieldValue(sourceDef, false, true);

      // If the _metrics_ planning redirect returns null or empty (e.g. for structural position
      // fields like "Position Count" whose _metrics_ value isn't written by the server), fall
      // back to the raw structural base value so the metric shows correctly instead of 0.
      const effectiveCurrentVal =
        currentVal == null || currentVal === "" ? (baseVal ?? currentVal) : currentVal;

      normalizedMetric.starting = baseVal != null && baseVal !== "" ? baseVal : effectiveCurrentVal;
      normalizedMetric.current = effectiveCurrentVal;
      normalizedMetric.target = aMetric?.targetValue;
      return normalizedMetric;
    });
  };

  // Re-run when formulas finish recalculating: UpdatePlanState updates fRootDBObj and
  // increments formulaTimestamp, which triggers this effect to pick up the new values.
  useEffect(() => {
    RebuildMetricsData();
  }, [planManager, chartDocument, wsMgr, planMgr?.GetFormulaTimestamp()]);

  const fieldColumnOptions = {
    columnStyle: { color: "#63646e", width: "34%" }
  };

  const valueColumnOptions = {
    columnStyle: { color: "#3d3d4a", width: "33%" }
  };

  let columns = [
    {
      field: "name",
      headerName: appstr("Name"),
      columnOptions: fieldColumnOptions
    },
    {
      field: "current",
      headerName: appstr("Current"),
      columnOptions: valueColumnOptions,
      type: "cellRenderer",
      CellRenderer: CurrentMetricText
    },
    {
      field: "target",
      headerName: appstr("Target"),
      columnOptions: valueColumnOptions,
      type: "cellRenderer",
      CellRenderer: TargetMetricInput
    }
  ];

  const gridOptions = {
    // set background colour on every row, this is probably bad, should be using CSS classes
    // set background colour on even rows again, this looks bad, should be using CSS classes
    getRowStyle: (data, rowIndex, isHovered) => {
      if (isHovered && canManageMetrics) {
        return { cursor: "pointer", background: "#ececed" };
      }
    },
    onRowClick: canManageMetrics ? onManageMetrics : undefined
  };

  const metricRecalcInProgress = planMgr.GetIsRecalculatingFormulas();

  return (
    <Flex direction={"column"}>
      <Flex className={"oc-dashboard-header"}>
        <Flex gap={"4px"} alignItems={"flex-end"}>
          <Text size={"small"} modifier={"medium"}>
            {appstr("Metrics")}
          </Text>
          {metricRecalcInProgress && (
            <Flex gap={"2px"} alignItems={"center"}>
              <Icon name={"arrows-rotate"} size={"xsmall"} />
              <Text size={"xsmall"} modifier={"lightgrey"}>
                {appstrp("Refreshing", "...")}
              </Text>
            </Flex>
          )}
        </Flex>
        {canManageMetrics && (
          <span>
            <Button
              dataTestId={"metrics-table-manage-metrics"}
              onClick={onManageMetrics}
              modifier={"secondary"}
              size={"small"}
            >
              {appstr("Manage metrics")}
            </Button>
          </span>
        )}
      </Flex>
      <div className={cx("oc-metrics-table", "ocn-list-scrollbar")}>
        <Table
          gridOptions={gridOptions}
          columns={columns}
          data={metricsData.data}
          showHeader={true}
          containerPadding={false}
          sortEnabled={true}
          rowSize={"mediumR"}
          textSize={"smallT"}
          customHeaderClass={"oc-table-header-display-small"}
          customTableClass={"oc-table-display-small"}
        />
      </div>
    </Flex>
  );
};
